import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaDesktop,
  FaDownload,
  FaHdd,
  FaSearch,
  FaServer,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTimes,
} from "react-icons/fa";

import { fetchAssetsList } from "../../api/overviewApi";
import AssetDetailModal, { getAssetTypeStyle, getFreshnessStyle } from "./AssetDetailModal";

const ACCENT = "#6366f1";
const PAGE_SIZE = 25;
const EXPORT_LIMIT = 500;

const COLUMNS = [
  { key: "hostname", label: "Hostname", sortable: true },
  { key: "asset_type", label: "Type", sortable: true },
  { key: "os_name", label: "Operating System", sortable: true },
  { key: "serial_number", label: "Serial", sortable: true },
  { key: "model", label: "Manufacturer / Model", sortable: true },
  { key: "ip_address", label: "IP Address", sortable: true },
  { key: "ram_gb", label: "RAM / Disk", sortable: true },
  { key: "last_contact", label: "Last Seen", sortable: true },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Recently seen" },
  { value: "stale", label: "Stale" },
];

const controlStyle = {
  borderRadius: 12,
  background: "var(--color-bg-card)",
  border: "1px solid var(--color-border)",
  color: "var(--color-text)",
  padding: "9px 34px 9px 12px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  minWidth: 150,
};

const SortIcon = ({ active, direction }) => {
  if (!active) return <FaSort size={9} style={{ opacity: 0.35 }} />;
  return direction === "asc" ? <FaSortUp size={9} color={ACCENT} /> : <FaSortDown size={9} color={ACCENT} />;
};

const formatLastSeen = (asset) => {
  if (!asset.last_contact) return "Never";
  const date = new Date(asset.last_contact);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const deviceIconFor = (assetType) => {
  if ((assetType || "").includes("server")) return FaServer;
  return FaDesktop;
};

/**
 * Managed asset inventory table. Search, sorting and paging are all resolved server-side
 * because a single client can report several hundred endpoints.
 */
const AssetsTable = ({ className, clientId, clientName, staleDays = 30 }) => {
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ asset_types: [], operating_systems: [] });

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [assetType, setAssetType] = useState("");
  const [osName, setOsName] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("hostname");
  const [sortDir, setSortDir] = useState("asc");

  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const isInitialLoad = useRef(true);
  const isMobile = window.innerWidth <= 992;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const applyFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const queryOptions = useMemo(
    () => ({ page, pageSize: PAGE_SIZE, search, assetType, osName, status, sortBy, sortDir }),
    [page, search, assetType, osName, status, sortBy, sortDir]
  );

  useEffect(() => {
    if (!clientId) {
      setAssets([]);
      setTotal(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (isInitialLoad.current) setLoading(true);
    else setIsRefetching(true);

    fetchAssetsList(clientId, queryOptions)
      .then((result) => {
        if (cancelled) return;
        setAssets(result?.assets || []);
        setTotal(result?.total || 0);
        setTotalPages(result?.total_pages || 0);
        if (result?.filters) setFilterOptions(result.filters);
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      })
      .catch(() => {
        if (cancelled) return;
        setAssets([]);
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, queryOptions]);

  const toggleSort = useCallback(
    (key) => {
      if (sortBy === key) {
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
        return;
      }
      setSortBy(key);
      setSortDir(key === "last_contact" ? "desc" : "asc");
    },
    [sortBy]
  );

  const hasActiveFilters = Boolean(search || assetType || osName || status);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setAssetType("");
    setOsName("");
    setStatus("");
    setPage(1);
  };

  const handleExportCSV = async () => {
    if (!clientId || total === 0 || exporting) return;
    setExporting(true);

    try {
      const result = await fetchAssetsList(clientId, { ...queryOptions, page: 1, pageSize: EXPORT_LIMIT });
      const rows = result?.assets || [];
      if (rows.length === 0) return;

      const headers = [
        "Hostname", "Type", "Operating System", "OS Version", "Serial",
        "Manufacturer", "Model", "CPU", "RAM (GB)", "Storage (GB)",
        "IP Address", "MAC Address", "Last Seen", "Days Since Contact",
      ];
      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const csvRows = rows.map((asset) => [
        escape(asset.hostname), escape(asset.asset_type_label), escape(asset.os_name),
        escape(asset.os_version), escape(asset.serial_number), escape(asset.manufacturer),
        escape(asset.model), escape(asset.cpu_model), asset.ram_gb ?? "", asset.storage_gb ?? "",
        escape(asset.ip_address), escape(asset.mac_address), escape(asset.last_contact),
        asset.days_since_contact ?? "",
      ]);

      const csvContent = [headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const safeClientName = (clientName || "All_Clients").replace(/[^a-z0-9]/gi, "_");

      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${safeClientName}_assets_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const cardShell = {
    borderRadius: 22,
    background: "var(--color-bg-card)",
    border: "1px solid var(--color-border)",
    boxShadow: "0 8px 24px color-mix(in srgb, var(--color-text) 5%, transparent)",
  };

  if (loading) {
    return (
      <Card style={cardShell}>
        <Card.Body>
          <div style={{ minHeight: "42dvh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p style={{ color: "var(--color-text-light)", margin: 0 }}>Loading asset inventory...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={className}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .assets-inventory-table thead th {
          background: color-mix(in srgb, var(--color-text-light) 6%, var(--color-bg-card)) !important;
        }
        .assets-inventory-table tbody tr {
          transition: background-color 0.18s ease;
          background: transparent !important;
        }
        .assets-inventory-table tbody td {
          background: transparent !important;
        }
        .assets-inventory-table tbody tr:hover {
          background: color-mix(in srgb, ${ACCENT} 5%, var(--color-bg-card)) !important;
        }
      `}</style>

      <Card style={cardShell}>
        <Card.Body style={{ padding: isMobile ? 16 : 22 }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3" style={{ flexWrap: "wrap", gap: 12 }}>
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  background: `color-mix(in srgb, ${ACCENT} 14%, var(--color-bg-card))`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FaHdd size={16} color={ACCENT} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "var(--color-text)", letterSpacing: "-0.01em" }}>
                  Asset Inventory
                </div>
                <div style={{ fontSize: 12, color: "var(--color-text-light)", fontWeight: 500, marginTop: 2 }}>
                  Search, filter, and open any managed device
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
              {total > 0 && (
                <button
                  onClick={handleExportCSV}
                  title={`Export up to ${EXPORT_LIMIT} rows to CSV`}
                  style={{
                    borderRadius: 999,
                    background: `color-mix(in srgb, ${ACCENT} 10%, var(--color-bg-card))`,
                    border: `1px solid color-mix(in srgb, ${ACCENT} 22%, transparent)`,
                    padding: "8px 16px",
                    minHeight: 40,
                    fontSize: 13,
                    cursor: exporting ? "wait" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: ACCENT,
                    fontWeight: 700,
                  }}
                >
                  <FaDownload size={11} />
                  {exporting ? "Exporting..." : "Export CSV"}
                </button>
              )}

              <div
                style={{
                  borderRadius: 999,
                  padding: "8px 16px",
                  minHeight: 40,
                  background: `color-mix(in srgb, ${ACCENT} 10%, var(--color-bg-card))`,
                  border: `1px solid color-mix(in srgb, ${ACCENT} 18%, transparent)`,
                  color: ACCENT,
                  fontSize: 13,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {total.toLocaleString()} {total === 1 ? "device" : "devices"}
              </div>
            </div>
          </div>

          {/* Filter toolbar */}
          <div
            className="d-flex align-items-center"
            style={{
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 16,
              padding: 12,
              borderRadius: 16,
              background: "color-mix(in srgb, var(--color-text-light) 5%, var(--color-bg-card))",
              border: "1px solid var(--color-border)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                borderRadius: 12,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                padding: "9px 12px",
                minWidth: isMobile ? 160 : 240,
                flex: isMobile ? "1 1 100%" : "0 1 260px",
              }}
            >
              <FaSearch size={12} color="var(--color-text-light)" />
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search hostname, serial, IP, model"
                style={{ border: "none", outline: "none", background: "transparent", color: "var(--color-text)", fontSize: 13, width: "100%" }}
              />
              {searchInput && (
                <FaTimes size={10} color="var(--color-text-light)" style={{ cursor: "pointer", flexShrink: 0 }} onClick={() => setSearchInput("")} />
              )}
            </div>

            <div className="position-relative d-flex align-items-center">
              <select value={assetType} onChange={applyFilter(setAssetType)} style={controlStyle}>
                <option value="">All types</option>
                {(filterOptions.asset_types || []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <FaChevronDown size={9} style={{ position: "absolute", right: 12, pointerEvents: "none", color: "var(--color-text-light)" }} />
            </div>

            <div className="position-relative d-flex align-items-center">
              <select value={osName} onChange={applyFilter(setOsName)} style={{ ...controlStyle, minWidth: 180 }}>
                <option value="">All operating systems</option>
                {(filterOptions.operating_systems || []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <FaChevronDown size={9} style={{ position: "absolute", right: 12, pointerEvents: "none", color: "var(--color-text-light)" }} />
            </div>

            <div className="position-relative d-flex align-items-center">
              <select value={status} onChange={applyFilter(setStatus)} style={controlStyle}>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <FaChevronDown size={9} style={{ position: "absolute", right: 12, pointerEvents: "none", color: "var(--color-text-light)" }} />
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  border: "none",
                  background: "color-mix(in srgb, #ef4444 10%, var(--color-bg-card))",
                  color: "#ef4444",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  borderRadius: 999,
                  padding: "8px 12px",
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Table */}
          <div
            style={{
              minHeight: "42dvh",
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "56dvh",
              WebkitOverflowScrolling: "touch",
              position: "relative",
              borderRadius: 16,
              border: "1px solid var(--color-border)",
            }}
          >
            <AnimatePresence>
              {isRefetching && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{ position: "absolute", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "color-mix(in srgb, var(--color-bg-card) 55%, transparent)" }}
                >
                  <div style={{ background: "var(--color-bg-card)", borderRadius: 12, padding: "14px 22px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", border: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(99,102,241,0.3)", borderTopColor: ACCENT, animation: "spin 0.8s linear infinite" }} />
                    <span style={{ color: "var(--color-text)", fontSize: 13, fontWeight: 500 }}>Loading...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {assets.length === 0 && !isRefetching ? (
              <div style={{ minHeight: "42dvh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <p style={{ color: "var(--color-text-light)", margin: 0, textAlign: "center" }}>
                  {!clientId
                    ? "Select a client to view their managed assets"
                    : hasActiveFilters
                      ? "No assets match the current filters"
                      : "No ConnectWise assets found for this client"}
                </p>
              </div>
            ) : (
              <Table hover responsive className="assets-inventory-table" style={{ marginBottom: 0, color: "var(--color-text)", width: "100%", backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                        style={{
                          color: "var(--color-text-light)",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          borderBottom: "1px solid var(--color-border)",
                          padding: "14px 14px",
                          whiteSpace: "nowrap",
                          cursor: column.sortable ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        <span className="d-inline-flex align-items-center gap-2">
                          {column.label}
                          {column.sortable && <SortIcon active={sortBy === column.key} direction={sortDir} />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => {
                    const typeStyle = getAssetTypeStyle(asset.asset_type);
                    const freshness = getFreshnessStyle(asset.days_since_contact, staleDays);
                    const DeviceIcon = deviceIconFor(asset.asset_type);

                    return (
                      <tr key={asset.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "14px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: 10,
                                background: `color-mix(in srgb, ${typeStyle.color} 14%, var(--color-bg-card))`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <DeviceIcon size={12} color={typeStyle.color} />
                            </div>
                            <button
                              onClick={() => setSelectedAsset(asset)}
                              style={{ background: "none", color: ACCENT, padding: 0, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", textDecoration: "none" }}
                              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                            >
                              {asset.hostname}
                            </button>
                            {asset.is_virtual && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#06b6d4", background: "rgba(6,182,212,0.12)", padding: "2px 7px", borderRadius: 999 }}>
                                VM
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              background: typeStyle.background,
                              color: typeStyle.color,
                              padding: "5px 11px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {asset.asset_type_label}
                          </span>
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", color: "var(--color-text)" }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{asset.os_name || "—"}</div>
                          {asset.os_display_version && (
                            <div style={{ color: "var(--color-text-light)", fontSize: 11, marginTop: 2 }}>{asset.os_display_version}</div>
                          )}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", color: "var(--color-text-light)", whiteSpace: "nowrap", fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {asset.serial_number || "—"}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", color: "var(--color-text)" }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{asset.manufacturer || "—"}</div>
                          {asset.model && (
                            <div style={{ color: "var(--color-text-light)", fontSize: 11, marginTop: 2 }}>{asset.model}</div>
                          )}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", color: "var(--color-text)", whiteSpace: "nowrap", fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {asset.ip_address || "—"}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", color: "var(--color-text)", whiteSpace: "nowrap", fontSize: 13 }}>
                          <span style={{ fontWeight: 700 }}>{asset.ram_gb ? `${asset.ram_gb} GB` : "—"}</span>
                          {asset.storage_gb ? (
                            <span style={{ color: "var(--color-text-light)", fontSize: 12 }}> / {asset.storage_gb} GB</span>
                          ) : null}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              background: freshness.background,
                              color: freshness.color,
                              padding: "5px 11px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {formatLastSeen(asset)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3" style={{ flexWrap: "wrap", gap: 12 }}>
              <span style={{ fontSize: 12, color: "var(--color-text-light)", fontWeight: 500 }}>
                Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                  style={{
                    borderRadius: 999,
                    border: "1px solid var(--color-border)",
                    background: "var(--color-bg-card)",
                    color: page <= 1 ? "var(--color-text-light)" : "var(--color-text)",
                    padding: "7px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: page <= 1 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: page <= 1 ? 0.5 : 1,
                  }}
                >
                  <FaChevronLeft size={9} /> Previous
                </button>

                <span
                  style={{
                    fontSize: 12,
                    color: ACCENT,
                    fontWeight: 700,
                    minWidth: 96,
                    textAlign: "center",
                    background: `color-mix(in srgb, ${ACCENT} 10%, var(--color-bg-card))`,
                    borderRadius: 999,
                    padding: "7px 12px",
                  }}
                >
                  Page {page} of {Math.max(totalPages, 1)}
                </span>

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages || 1))}
                  disabled={page >= totalPages}
                  style={{
                    borderRadius: 999,
                    border: "1px solid var(--color-border)",
                    background: "var(--color-bg-card)",
                    color: page >= totalPages ? "var(--color-text-light)" : "var(--color-text)",
                    padding: "7px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: page >= totalPages ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: page >= totalPages ? 0.5 : 1,
                  }}
                >
                  Next <FaChevronRight size={9} />
                </button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <AnimatePresence>
        {selectedAsset && (
          <AssetDetailModal asset={selectedAsset} staleDays={staleDays} onClose={() => setSelectedAsset(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AssetsTable;
